import { Checklist, Download, ExpandMore, FileUpload, Refresh } from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { Accordion, AccordionActions, AccordionDetails, AccordionSummary, Alert, Avatar, Button, Checkbox, CircularProgress, Divider, IconButton, ListItem, ListItemButton, ListItemIcon, ListItemText, Typography, useMediaQuery } from '@mui/material';
import List from '@mui/material/List';
import { Box } from '@mui/system';
import { compact, each, find } from 'lodash';
import { Fragment, MouseEventHandler, useEffect, useRef, useState } from 'react';
import { useLocation, useParams, useSearchParams } from 'react-router-dom';
import { track, useContent, useFileDownload, useMergedFileDownload, useNavigateWithSearchParams } from '../../logic';
import {
  AdHocDoc,
  AttachmentDownload, DownloadLinkType,
  PortalNotification,
  PortalNotificationReference,
  PortalOrderFileUploadType,
  PortalUpload,
} from '../../types'
import { formatDate, isPdfFileName, toTitleCase } from '../../utils';
import { useOrganization } from '../OrganizationProvider';
import { Section } from '../shared/Section';
import { useFileUpload } from './useFileUpload';
import { SectionConfig } from './ClosingPageSectionConfig';
import { sortByDate, sortByName } from '../../utils/sortBy';
import { UploadDialog } from './UploadDialog';
import { useGetOrder, useGetUploads } from '../../logic/useApiQuery';
import { useAppTheme } from '../../logic/useAppTheme';
import { isAdhocPortalOrder } from '../../utils/isAdhocPortalOrder';
import { useIsFeatureEnabled } from '../../logic/useFeatureFlags';
import { MergeSelectionItem, MIN_MERGE_DOCUMENTS, useDocumentMergeSelection } from './useDocumentMergeSelection';

export const DOCUMENT_MERGE_FEATURE_FLAG = 'documentmerge'

const uploadTypeMap: Record<string, string> = {
  'CSForm_': 'Completed Form',
  'CSSignedForm_': 'E-Signed Form',
  'Completion_Cert': 'E-Sign Completion Certificate',
  'esign_policy_consent': 'E-Sign Policy Consent',
  'Audit Trail': 'E-Sign Audit Trail',
  'CSPayment-Agreement-Receipt': 'Earnest Money User Agreement and Receipt',
  'CSPayment-Deposit': 'Earnest Money Deposit Slip',
  'ESignCertificate': 'E-Signed Form Certificate of Completion',
  'CSPayment-CTC-Receipt': 'Cash to Close Receipt',
  'Summary': 'E-Signed Form - Audit Summary'
}

// These are docs created through form and e-signing tasks. The file names are not
// useful to the end user
const typesToIgnoreFileName = [
  'CSForm_',
  'CSSignedForm_',
  'Completion_Cert',
  'esign_policy_consent',
]

function getWiringInstructionsType(code: string, filename: string, WiringInstructionsText: string) {
  const wiringInstructionsUploadTypeMap: Record<string, string> = {
    ...uploadTypeMap,
    'CSForm_': WiringInstructionsText,
    'CSSignedForm_': WiringInstructionsText,
  }

  let displayType = wiringInstructionsUploadTypeMap[code]

  // Esign related docs all have the same type so override some based on filename
  each(wiringInstructionsUploadTypeMap, (val, key) => {
    if (filename.includes(key)) displayType = val
  })

  return displayType
}

function getTypeName(documentType: string, documentFileName: string, types: PortalOrderFileUploadType[], notificationType: string, WiringInstructionsText: string) {
  // type exactly matches a type code defined in admin token.portal_data_file_upload_types
  let labelFromAdminToken = find(types, { Code: documentType })?.Label

  // filename includes a code defined in admin token.portal_data_file_upload_types
  // Esign related docs share the same type and filenames have UUIDs included so
  // we can't count on an exact match.
  each(types, (type) => {
    if (documentFileName.includes(type.Code)) {
      labelFromAdminToken = type.Label
    }
  })

  // Prioritize token defined labels
  if (labelFromAdminToken) {
    return labelFromAdminToken
  }

  let displayType

  if (notificationType === 'WiringInstructionsTask') {
    displayType = getWiringInstructionsType(documentType, documentFileName, WiringInstructionsText)
  } else {
    // Default labels we defined for form and esigning docs
    displayType = uploadTypeMap[documentType]

    // Esign related docs all have the same type so override some based on filename
    each(uploadTypeMap, (val, key) => {
      if (documentFileName.includes(key)) displayType = val
    })
  }



  return displayType || 'Unknown type'
}

function getFileName(upload: PortalUpload) {
  if (typesToIgnoreFileName.includes(upload.DocumentType)) {
    return ''
  } else {
    return upload.OriginalDocumentName
  }
}

function getDownloadLinkType(file: AttachmentDownload | AdHocDoc): DownloadLinkType {
  if (file.StepId !== undefined) {
    return 'document'
  } else if (file.UploadType === 'ReplyAttachment') {
    return 'reply'
  } else {
    return 'adhoc'
  }
}

function getDisplayName(fileName: string) {
  try {
    // decode can throw and this display only name doesn't seem worth that risk
    // could track down where we encode these instead of this... :(
    return decodeURIComponent(fileName)
  } catch (error) {
    return fileName
  }
}

function isPdfUpload(upload: PortalUpload) {
  return isPdfFileName(upload.DocumentFileName) || isPdfFileName(upload.OriginalDocumentName)
}

/**
 * Number of documents in the section that could be picked for a merge.
 */
export function countMergeablePdfs(docs: (AttachmentDownload | AdHocDoc)[], uploads: PortalUpload[]) {
  const pdfDocs = docs.filter(doc => isPdfFileName(doc.FileName)).length
  const pdfUploads = uploads.filter(isPdfUpload).length
  return pdfDocs + pdfUploads
}

/**
 * Props passed to document rows while the user is choosing PDFs to merge.
 * When omitted the row behaves as a normal download button.
 */
export type DocumentSelectionProps = {
  /** True when the document is a PDF and can be added to the merge. */
  selectable: boolean;
  selected: boolean;
  /** 1-based position in the merge order, when selected. */
  index?: number;
  onToggle: () => void;
}

export function ClosingDocumentsSection({ collapsible, closingDocuments, CompanyIdOrderIdHash, sectionConfig, notificationRefs }: {
  collapsible?: boolean;
  closingDocuments: (AttachmentDownload | AdHocDoc)[];
  CompanyIdOrderIdHash: string;
  sectionConfig: SectionConfig;
  notificationRefs: PortalNotificationReference[];
}) {
  const { pathname } = useLocation()
  const navigateWithSearchParams = useNavigateWithSearchParams()
  const org = useOrganization()
  const fileUploadsEnabled = org.FileUploads === 'enabled'
  const { closingId, section, id } = useParams()
  const theme = useAppTheme() 
  const isSmall = useMediaQuery(theme.breakpoints.down('sm'));
  const isClosingDocSection = section === 'closing-documents'
  const isDocUpload = isClosingDocSection && id === 'upload'
  const [showUploadDialog, setShowUploadDialog] = useState(isDocUpload && !isSmall);

  const { uploadInput, fileUploadBody, fileUploadActions, resetFileUploadHook, isComplete, files } = useFileUpload({})

  useEffect(() => {
    setShowUploadDialog(isDocUpload && !isSmall)

    // reset the files hook if we navigate away. minimizes potential odd states
    if (!isDocUpload && files.length) {
      resetFileUploadHook()
    }
  }, [isDocUpload, isSmall, setShowUploadDialog, resetFileUploadHook, files])

  const handleUploadClick = (e: any) => {
    if (isComplete) {
      // reset for a fresh start if we've already completed uploads and click to add more
      resetFileUploadHook()
    }

    if (!isDocUpload) {
      navigateWithSearchParams(`/closings/${closingId}/closing-documents/upload`)
    }

    if (isSmall) {
      e.preventDefault()
    } else {
      setShowUploadDialog(true)
    }
  }

  const handleUploadDialogClose = (e: any) => {
    e.preventDefault()
    setShowUploadDialog(false)
    const parentPathname = pathname.split('/').slice(0, -1).join('/')
    navigateWithSearchParams(parentPathname)
  }

  const uploadButton = <Button onClick={handleUploadClick} component="label">Upload{uploadInput}</Button>

  return (
    <>
      {isSmall && isDocUpload ? (
        <>
          <Typography
            variant="h5"
            sx={{ fontWeight: "500", lineHeight: "22px", m: 1 }}
          >
            Upload
          </Typography>
          <Divider />
          {fileUploadBody}
          <br />
          {fileUploadActions}
        </>
      ) : (
        <Section
          title={sectionConfig.name}
          icon={sectionConfig.icon}
          collapsible={collapsible}
          isSelected={isClosingDocSection}
          secondaryAction={fileUploadsEnabled && uploadButton}
        >
          <ClosingDocumentsList
            closingDocuments={closingDocuments}
            CompanyIdOrderIdHash={CompanyIdOrderIdHash}
            fileUploadsEnabled={fileUploadsEnabled}
            handleUploadClick={handleUploadClick}
            notificationRefs={notificationRefs}          />
        </Section>
      )}

      <UploadDialog
        open={showUploadDialog}
        onClose={handleUploadDialogClose}
        fileUploadBody={fileUploadBody}
        fileUploadActions={fileUploadActions}
        isComplete={isComplete}
      />
    </>
  );
}

export function ClosingDocumentsList({ closingDocuments, CompanyIdOrderIdHash, fileUploadsEnabled, handleUploadClick, notificationRefs }: {
  closingDocuments: (AttachmentDownload | AdHocDoc)[];
  CompanyIdOrderIdHash: string;
  fileUploadsEnabled: boolean;
  handleUploadClick: MouseEventHandler;
  notificationRefs: PortalNotificationReference[];
}) {
  let [searchParams] = useSearchParams();
  let { search } = useLocation()
  const { closingId } = useParams()
  const component = searchParams.get('component')
  const { data: globalOrderData } = useGetOrder(closingId ?? '', search ?? '')
  const orderDataRole = globalOrderData?.userMappingInfo?.role
  const currentRoleParam = searchParams.get("role");

  if (!currentRoleParam) {
    searchParams.set("role", orderDataRole || '');
    search = `?${searchParams.toString()}`
  }
  const { data = [], error, isFetching, refetch } = useGetUploads(CompanyIdOrderIdHash, search);
  const uploads = data

  const [expanded, setExpanded] = useState(false)
  const uploadsRef = useRef<null | HTMLDivElement>(null)

  const mergeEnabled = useIsFeatureEnabled(DOCUMENT_MERGE_FEATURE_FLAG)
  const selection = useDocumentMergeSelection()
  const { merge, isMerging, error: mergeError, reset: resetMerge } = useMergedFileDownload({
    CompanyIdOrderIdHash,
    onDownloaded: selection.cancel,
  })

  useEffect(() => {
    if(uploadsRef?.current && component === 'uploads') {
      setExpanded(true)
      uploadsRef.current.scrollIntoView({behavior: 'smooth', block: 'start'})
    }
  }, [component, uploadsRef, isFetching])

  if (error) {
    track('error', 'useGetUploads', {}, {
      message: JSON.stringify(error),
      component: 'ClosingDocumentsList',
    })
  }

  if (!closingDocuments?.length) {
    closingDocuments = []
  }
  //separate adhoc from download so that we can reduce downloads, then add adhocs back in
  const adHocDocs = closingDocuments.filter(doc => doc.StepId === undefined)
  const attachmentDownloads = closingDocuments.filter(doc => doc.StepId !== undefined)

  // NOTE: This is maybe kinda gross but I like an excuse to use reduce :simple-smile:
  let filteredDocs:(AttachmentDownload | AdHocDoc)[] = attachmentDownloads.reduce((prev: any, curr: any) => {
    const currFileName = curr.FileName
    const prevDoc = prev[currFileName]

    if (prevDoc) { 
      if ((Date.parse(curr.LastModifiedDateTime) || 0) > (Date.parse(prevDoc.LastModifiedDateTime) || 0)) {
        prev[currFileName] = curr
      }
    } else {
      prev[currFileName] = curr
    }

    return prev

    // TODO: typescript things. Rushing and not wanting to dig into it but I know
    // theres some fancy business to set the reduce prev object literal to...
    // key: limit to strings from AttachmentDownload.FileNames from closingDocuments
    // val: AttachmentDownload
  }, {} as any)

  filteredDocs = Object.values(filteredDocs)

  filteredDocs = filteredDocs.concat(adHocDocs)

  const sortedDocs:(AttachmentDownload | AdHocDoc)[] = filteredDocs.sort((a: AttachmentDownload|AdHocDoc, b: AttachmentDownload|AdHocDoc) => {
    const dateSort = sortByDate(a.LastModifiedDateTime, b.LastModifiedDateTime)
    if (dateSort) return dateSort
    return sortByName(a.FileName, b.FileName)
  })

  const sortedUploads:PortalUpload[] = uploads.sort((a: PortalUpload, b: PortalUpload) => {
    const dateSort = sortByDate(a.UploadDate, b.UploadDate)
    if (dateSort) return dateSort
    return sortByName(a.OriginalDocumentName, b.OriginalDocumentName)
  })

  function onClickExpand(e: any) {
    e.preventDefault()
    if (!expanded && !isFetching) refetch()
    setExpanded(!expanded)
  }

  function onClickRefresh(e: any) {
    e.preventDefault()
    setExpanded(true)
    if (!isFetching) refetch()
  }

  function onClickStartSelecting() {
    track('Merge documents select started')
    resetMerge()
    selection.start()

    // Show the uploads & task documents too so every PDF is available to pick
    if (fileUploadsEnabled && !expanded) {
      setExpanded(true)
      if (!isFetching) refetch()
    }
  }

  function onClickCancelSelecting() {
    track('Merge documents select cancelled', '', {}, { fileCount: selection.count })
    resetMerge()
    selection.cancel()
  }

  function onClickMerge() {
    if (!selection.canMerge || isMerging) return
    merge(selection.selected.map(item => item.file))
  }

  /**
   * Builds the row selection props for a document, or undefined when the user
   * isn't currently picking documents to merge.
   */
  function getSelectionProps(item: MergeSelectionItem, isPdf: boolean): DocumentSelectionProps | undefined {
    if (!selection.isSelecting) return undefined

    return {
      selectable: isPdf,
      selected: selection.isSelected(item.key),
      index: selection.selectionIndex(item.key),
      onToggle: () => selection.toggle(item),
    }
  }

  // Only offer merging when there are actually enough PDFs to merge. Uploads
  // are counted once loaded; the toolbar appears as soon as the data arrives.
  const pdfCount = countMergeablePdfs(sortedDocs, fileUploadsEnabled ? sortedUploads : [])
  const showMergeToolbar = mergeEnabled && pdfCount >= MIN_MERGE_DOCUMENTS

  return (
    <>
      {showMergeToolbar &&
        <MergeDocumentsToolbar
          isSelecting={selection.isSelecting}
          count={selection.count}
          canMerge={selection.canMerge}
          isMerging={isMerging}
          error={mergeError}
          onStart={onClickStartSelecting}
          onCancel={onClickCancelSelecting}
          onMerge={onClickMerge}
          onDismissError={resetMerge}
        />
      }

      {!closingDocuments.length ?
        <Box sx={{ m: 1 }}>No documents at this time</Box> :
        <List>
          {sortedDocs.map((file, i) => {
            const fileType = getDownloadLinkType(file)
            const item: MergeSelectionItem = {
              key: `${fileType}:${file.FileName}`,
              displayName: getDisplayName(file.FileName),
              file: { FileName: file.FileName, Type: fileType },
            }
            return (
              <Fragment key={i}>
                <Divider light={true} sx={{ mr: 1, ml: 1 }}/>
                <FileItem
                  file={file}
                  CompanyIdOrderIdHash={CompanyIdOrderIdHash}
                  selection={getSelectionProps(item, isPdfFileName(file.FileName))}
                />
              </Fragment>
            )
          })}
        </List>
      }

      {fileUploadsEnabled &&
        <>
          <Divider />
          <Accordion
            expanded={expanded}
            disableGutters={true}
            sx={{ boxShadow: 'none', '&.Mui-disabled': { backgroundColor: 'inherit'} }}
          >
            <AccordionSummary
              ref={uploadsRef}
              onClick={(e: any) => e.preventDefault()}
              expandIcon={<IconButton onClick={onClickExpand}><ExpandMore/></IconButton>}
              sx={{
                scrollMarginTop: {xs: '70px', sm: '0px'},
                '&:hover:not(.Mui-disabled)': { cursor: 'default' },
                '.MuiAccordionSummary-content': { justifyContent: 'space-between'},
              }}
            >
              {!!error ?
                <Typography variant="body2" sx={{ fontWeight: '500', color: 'red' }}>There was a problem getting your uploads and task documents. Try clicking <Refresh fontSize="small" /> to retry.</Typography> :
                <Typography variant="body2" sx={{ fontWeight: '500', color: 'primary.main' }}>Uploads &amp; Task Documents</Typography>
              }
              <AccordionActions sx={{ height: '24px', alignSelf: 'center' }}>
                <IconButton onClick={handleUploadClick} >
                  <FileUpload />
                </IconButton>
                <IconButton onClick={onClickRefresh} >
                  {isFetching ? <CircularProgress size={24} /> : <Refresh />}
                </IconButton>
              </AccordionActions>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0, pl: 0 }}>
              <List>
                {uploads.length ?
                  sortedUploads.map((upload, i) => {
                    const notificationRef = notificationRefs.find((ref) => ref.NotificationId === upload.NotificationId)
                    const item: MergeSelectionItem = {
                      key: `upload:${upload.DocumentBatchId}:${upload.DocumentFileName}`,
                      displayName: upload.OriginalDocumentName || upload.DocumentFileName,
                      file: {
                        FileName: upload.DocumentFileNameForUpload,
                        Type: 'upload',
                        DocumentBatchId: upload.DocumentBatchId,
                        DocumentFileName: upload.DocumentFileName,
                      },
                    }
                    const isPdf = isPdfUpload(upload)
                    return (
                      <Fragment key={i}>
                        <Divider light={true}/>
                        <UploadFileItem
                          upload={upload}
                          CompanyIdOrderIdHash={CompanyIdOrderIdHash}
                          notificationRef={notificationRef}
                          includeNotificationTitle={true}
                          selection={getSelectionProps(item, isPdf)}
                        />
                      </Fragment>
                    )
                  }) :
                  <Box sx={{ml: 2}}>None found. Try clicking <Refresh fontSize="small" /> to find recent uploads and task documents.</Box>
                }
              </List>
            </AccordionDetails>
          </Accordion>
        </>
      }
    </>
  )
}

/**
 * Entry point and action bar for merging PDFs. Shows a "Select PDFs to merge"
 * button normally, and the selection count with Merge & Download / Cancel
 * actions while the user is picking documents.
 */
export function MergeDocumentsToolbar({ isSelecting, count, canMerge, isMerging, error, onStart, onCancel, onMerge, onDismissError }: {
  isSelecting: boolean;
  count: number;
  canMerge: boolean;
  isMerging: boolean;
  error?: { userErrorMessage?: string; message?: string } | null;
  onStart: () => void;
  onCancel: () => void;
  onMerge: () => void;
  onDismissError: () => void;
}) {
  if (!isSelecting) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
        <Button size="small" startIcon={<Checklist />} onClick={onStart}>
          Select PDFs to merge
        </Button>
      </Box>
    )
  }

  const remaining = Math.max(MIN_MERGE_DOCUMENTS - count, 0)
  const helperText = remaining > 0
    ? `Select ${remaining} more PDF${remaining === 1 ? '' : 's'} to merge. They'll be combined in the order you select them.`
    : `${count} PDFs will be combined in the order you selected them.`

  return (
    <Box
      role="toolbar"
      aria-label="Merge documents"
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        p: 1.5,
        mb: 1,
        borderRadius: '12px',
        bgcolor: 'action.hover',
      }}
    >
      <Typography variant="body2">{helperText}</Typography>

      {error &&
        <Alert severity="error" onClose={onDismissError}>
          {error.userErrorMessage || error.message || 'We were unable to merge those documents. Please try again.'}
        </Alert>
      }

      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Typography variant="body2" sx={{ fontWeight: '500' }} aria-live="polite">
          {count} selected
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button size="small" onClick={onCancel} disabled={isMerging}>
            Cancel
          </Button>
          <LoadingButton
            size="small"
            variant="contained"
            startIcon={<Download />}
            onClick={onMerge}
            disabled={!canMerge}
            loading={isMerging}
            loadingPosition="start"
          >
            Merge &amp; Download
          </LoadingButton>
        </Box>
      </Box>
    </Box>
  )
}

function SelectionCheckbox({ selection, displayName }: { selection: DocumentSelectionProps; displayName: string }) {
  return (
    <ListItemIcon sx={{ minWidth: '40px' }}>
      <Checkbox
        edge="start"
        checked={selection.selected}
        disabled={!selection.selectable}
        tabIndex={-1}
        disableRipple
        inputProps={{ 'aria-label': `Select ${displayName} for merge` }}
      />
    </ListItemIcon>
  )
}

function SelectionOrderBadge({ selection }: { selection: DocumentSelectionProps }) {
  return (
    <ListItemIcon sx={{ minWidth: '40px', pl: 2, justifyContent: 'flex-end' }}>
      {selection.selected && selection.index !== undefined ?
        <Avatar
          sx={{ width: 26, height: 26, fontSize: '0.8rem', fontWeight: 'bold', bgcolor: 'primary.main' }}
          aria-label={`Merge position ${selection.index}`}
        >
          {selection.index}
        </Avatar> :
        null
      }
    </ListItemIcon>
  )
}

const NOT_PDF_TEXT = 'Only PDF documents can be merged'

function FileItem({ file, CompanyIdOrderIdHash, selection }: {
  file: AttachmentDownload|AdHocDoc;
  CompanyIdOrderIdHash: string;
  selection?: DocumentSelectionProps;
}) {
  const fileType = getDownloadLinkType(file)
  const { triggerDownload, isDownloading, setIsDownloading } = useFileDownload({
    CompanyIdOrderIdHash,
    FileName: file.FileName,
    Type: fileType,
  })

  const date = formatDate({dateLike: file.LastModifiedDateTime}) || ''
  const stepTitle = file.StepTitle || file.Title

  const onClick = function() {
    if (selection) {
      if (selection.selectable) selection.onToggle()
      return
    }
    setIsDownloading(true)
    triggerDownload()
  }

  const displayName = getDisplayName(file.FileName)
  const isDisabled = selection ? !selection.selectable : isDownloading
  const showNotPdf = !!selection && !selection.selectable

  return (
    <ListItem sx={{ pl: 0, pr: 0 }}>
      <ListItemButton
        disabled={isDisabled}
        onClick={onClick}
        sx={{ pt: 0, pb: 0, minHeight: '66px' }}
        selected={!!selection?.selected}
        aria-pressed={selection ? selection.selected : undefined}
      >
        {selection && <SelectionCheckbox selection={selection} displayName={displayName} />}
        <ListItemText
          sx={{ wordBreak: 'break-word' }}
          primary={displayName}
          secondary={<> {stepTitle} <br/> {date} {showNotPdf && <><br/>{NOT_PDF_TEXT}</>} </>}
          disableTypography={!stepTitle && !date && !showNotPdf}
          secondaryTypographyProps={{ variant: 'light', fontSize: 'x-small', ml: 1, }}
        />
        {selection ?
          <SelectionOrderBadge selection={selection} /> :
          <ListItemIcon sx={{ minWidth: '40px', pl: 2 }}>
            { isDownloading ? <CircularProgress size={25} /> : <Download /> }
          </ListItemIcon>
        }
      </ListItemButton>
    </ListItem >
  )
}

export function UploadFileItem({ upload, CompanyIdOrderIdHash, notificationRef, notification, includeNotificationTitle, selection }: {
  upload: PortalUpload;
  CompanyIdOrderIdHash: string;
  notification?: PortalNotification;
  notificationRef?: PortalNotificationReference;
  includeNotificationTitle?: boolean;
  selection?: DocumentSelectionProps;
}) {
  const { closingId } = useParams()
  const { search } = useLocation()
  const { data: globalOrderData } = useGetOrder(closingId ?? '', search ?? '')
  const categories = (globalOrderData && !isAdhocPortalOrder(globalOrderData.Order) && compact(globalOrderData?.Order.FileUploadTypes)) || []
  const date = formatDate({dateLike: upload.UploadDate}) || ''
  const { WiringInstructionsText } = useContent();

  const { triggerDownload, isDownloading, setIsDownloading } = useFileDownload({
    CompanyIdOrderIdHash,
    FileName: upload.DocumentFileNameForUpload,
    Type: 'upload',
    DocumentBatchId: upload.DocumentBatchId,
    DocumentFileName: upload.DocumentFileName,
  })


  const onClick = function() {
    if (selection) {
      if (selection.selectable) selection.onToggle()
      return
    }
    setIsDownloading(true)
    triggerDownload()
  }

  const notificationTitle = notificationRef?.NotificationTitle || notification?.Title || 'Manual Upload'
  const notificationType = notification?.Type || notificationRef?.Type || ''
  const displayType = getTypeName(upload.DocumentType, upload.DocumentFileName, categories, notificationType, toTitleCase(WiringInstructionsText))
  const isDisabled = selection ? !selection.selectable : isDownloading
  const showNotPdf = !!selection && !selection.selectable

  function getPrimaryTypography() {
    return (
      <Box sx={{ display: 'flex', flexWrap: 'wrap', width: '100%' }}>
        {includeNotificationTitle && (
          <Box>
            {notificationTitle}&nbsp;-&nbsp;
          </Box>
        )}
        <Box>
          {displayType}
        </Box>
      </Box>
    )

  }

  function getSecondaryTypography() {
    const filename = getFileName(upload)

    const lines = [
      filename ? filename : null,
      date ? date : null,
      showNotPdf ? NOT_PDF_TEXT : null,
    ].filter(Boolean)

    return <>
      {lines.map((line, i) => <Fragment key={i}>{line}<br/></Fragment>)}
    </>
  }

  const accessibleName = getFileName(upload) || `${notificationTitle} ${displayType}`

  return (
    <ListItem>
      <ListItemButton
        disabled={isDisabled}
        onClick={onClick}
        sx={{ pt: 0, pb: 0, pl: 1, pr: 1, minHeight: '66px' }}
        selected={!!selection?.selected}
        aria-pressed={selection ? selection.selected : undefined}
      >
        {selection && <SelectionCheckbox selection={selection} displayName={accessibleName} />}
        <ListItemText
          sx={{ wordBreak: 'break-word' }}
          primaryTypographyProps={{ fontSize: 'default' }}
          primary={getPrimaryTypography()}
          secondary={getSecondaryTypography()}
          secondaryTypographyProps={{ variant: 'light', fontSize: 'x-small' }}
        />
        {selection ?
          <SelectionOrderBadge selection={selection} /> :
          <ListItemIcon sx={{ minWidth: '40px', pl: 2 }}>
            { isDownloading ? <CircularProgress size={25} /> : <Download /> }
          </ListItemIcon>
        }
      </ListItemButton>
    </ListItem >
  )
}
